import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCatalogDraft } from '../../hooks/useCatalogDraft';
import { publishQuarters } from '../../pricing/publishQuarters';
import { getCurrentQuarter } from '../../constants/quarterConfig';
import { isCostKind } from '../../catalog/costDraft';
import { getVendor } from '../../config/vendorRegistry';
import { ReviewPanel } from './ReviewPanel';
import { ProductsView } from './ProductsView';
import { ImpactView } from './ImpactView';
import { GridView } from './GridView';

/**
 * The manual entry point (07-29 spec §12): a tab shell over three ways to see
 * the same catalog — `ProductsView` (SP4, the default landing tab: every
 * offered SKU, read-only), `ImpactView` (the editing surface, split component
 * / live-ripple layout) and `GridView` (the demoted component x quarter grid)
 * — the latter two landing an edit on the SAME `ReviewPanel` the CSV import
 * (Task 11) lands on.
 *
 * The state itself lives one level down, in `useCatalogDraft`: this page only
 * assembles the pieces (vendor, categories, the selected quarter) and wires
 * `stage`/`commit`/`resolveAlias` through. An edit becomes a one-cell
 * `CostDraft` in either view, exactly as `ImportPage.jsx` produces one from a
 * sheet — that shared shape is what keeps this off a second path into the
 * catalog.
 */

export function CatalogPage({ vendor, user }) {
  const {
    status, components, seed, review, stage, commit, resolveAlias, error, preview, discard,
  } = useCatalogDraft(vendor);

  // SP4: Products is the landing view, superseding SP3's Impact-first default.
  // The catalog is read at two levels and the outer one comes first — what
  // products exist, then the component costs underneath them. Impact is still
  // the editing surface and still precedes Grid.
  const [tab, setTab] = useState('products');
  // Whole-branch review finding F2: `publishQuarters(vendor)[0]` (the OLDEST
  // publish quarter — AIC's list runs 2025-Q3..2028-Q4) opened the editor a
  // year in the past, where every decomposition row is unset. Defaults to
  // the current quarter instead, matching what a PM actually means to edit
  // today; falls back to the list's last entry on the off chance the current
  // quarter isn't itself a publish quarter (e.g. it runs past the sheet's
  // horizon), rather than defaulting to an index that may not exist.
  const [quarter, setQuarter] = useState(() => {
    const list = publishQuarters(vendor);
    const current = getCurrentQuarter();
    return list.includes(current) ? current : list[list.length - 1];
  });
  const categories = getVendor(vendor).capabilities.catalogCategories ?? [];
  const editableCategories = getVendor(vendor).capabilities.catalogEditableCategories ?? [];

  // Page-local, mirroring ImportPage.jsx: the hook's own lock (`busy.current`)
  // is a ref, not state, so it cannot drive a `disabled` attribute here.
  const [busy, setBusy] = useState(false);

  const quarters = useMemo(() => publishQuarters(vendor), [vendor]);

  /**
   * COST rows only — the same `COST_KINDS` set `planWrites` refuses to write
   * outside of (`costDraft.ts`), so `ReviewPanel`'s alias resolver never
   * offers a percentage-kind SKU as a target (whole-branch review finding
   * C1, applied to the one place left here that still lists SKUs: the
   * dropdown a PM uses to resolve an unmatched CSV ref).
   */
  const skus = useMemo(
    () => [...new Set(
      (components ?? []).filter((c) => isCostKind(c.kind)).map((c) => c.componentSku),
    )].sort(),
    [components],
  );

  // Mirrors ImportPage.jsx's own `withBusy`: one local flag around
  // commit/resolveAlias so the review panel's buttons disable during a write,
  // deliberately NOT wrapping `saveProfile`-style fire-and-forget calls,
  // because this page has none.
  const withBusy = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = () => withBusy(() => commit({ actor: user?.username }));
  const handleResolveAlias = (input) => withBusy(() => resolveAlias({ ...input, actor: user?.username }));
  // Synchronous and write-free (useCatalogDraft.discard), so no busy wrap:
  // the panel already disables Reset while a commit is in flight.
  const handleReset = () => discard();

  if (status !== 'ready') {
    return <div>{status === 'error' ? 'Failed to load the catalog.' : 'Loading catalog…'}</div>;
  }

  return (
    <div>
      <h1 className="text-xl font-bold mb-4">Catalog — {vendor}</h1>

      {/* Once a review exists, ReviewPanel owns showing `error` beside it — a
          top-level banner AND the panel's own would say the same thing
          twice, mirroring ImportPage.jsx. */}
      {!review && error && (
        <div className="text-red-400 mb-3" role="alert">{error}</div>
      )}

      <div className="mb-3 flex items-center gap-2">
        <label htmlFor="catalog-quarter" className="text-sm text-gray-400">Quarter</label>
        <select
          id="catalog-quarter" value={quarter} onChange={(e) => setQuarter(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5"
        >
          {quarters.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
        {/* A navigation, styled as one: solid like the page's primary
            actions, with a trailing arrow so it reads as "go to the import
            page" rather than a toggle on this one. */}
        <Link
          to="/admin/catalog/import"
          className="ml-2 bg-orange-600 hover:bg-orange-500 rounded px-3 py-1 text-sm font-medium"
        >
          Import Prices →
        </Link>
      </div>

      <div role="tablist" aria-label="Catalog views" className="mb-3 flex gap-2">
        {[['products', 'Products'], ['impact', 'Impact'], ['grid', 'Grid']].map(([id, label]) => (
          <button
            key={id} id={`catalog-tab-${id}`} type="button" role="tab"
            aria-selected={tab === id} aria-controls={`catalog-panel-${id}`}
            onClick={() => setTab(id)}
            className={tab === id ? 'text-orange-400 border-b-2 border-orange-400' : 'text-gray-400'}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Whole-branch review finding F8: the tabs had `role="tab"` and
          `aria-selected` but nothing on the panel side — a screen reader
          announced a tab with nothing attached. One wrapper carries whichever
          panel is actually mounted (only one view renders at a time, same as
          before this fix), so its `id` always matches the SELECTED tab's
          `aria-controls` and its `aria-labelledby` always resolves to a real
          tab. Arrow-key roving focus is a separate, larger change and out of
          this fix's scope. */}
      <div role="tabpanel" id={`catalog-panel-${tab}`} aria-labelledby={`catalog-tab-${tab}`}>
        {tab === 'products' ? (
          <ProductsView vendor={vendor} quarter={quarter} seed={seed} />
        ) : tab === 'impact' ? (
          <ImpactView
            vendor={vendor} user={user} seed={seed} components={components}
            categories={categories} editableCategories={editableCategories}
            quarter={quarter} preview={preview}
            stage={stage} busy={busy}
          />
        ) : (
          <GridView
            vendor={vendor} user={user} seed={seed} components={components}
            categories={categories} editableCategories={editableCategories}
            quarters={quarters} stage={stage} busy={busy}
          />
        )}
      </div>

      {review && (
        <ReviewPanel
          review={review}
          componentSkus={skus}
          onCommit={handleCommit}
          onResolveAlias={handleResolveAlias}
          onReset={handleReset}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
}

export default CatalogPage;
